import { uploadFileWrite, uploadFileClose } from "./fileserver/file";
import {
    createFragmentUploadFile,
    createThumbnailUploadFile,
    createVariantUploadFile,
} from "./site/fragment";
import { createMediaUploadFile } from "./site/mediafile";
import { UploadFile, Result } from "core/models";
import { MediaFileType } from "core/models/MediaFile";

const UPLOAD_CHUNK_SIZE = 1024 * 1024 * 1; // 1MiB

class UploadManager {
    uploads: UploadFile[];

    accessToken: string;

    projectID: number;

    constructor(accessToken: string, projectID: number) {
        this.accessToken = accessToken;
        this.projectID = projectID;
        this.uploads = [];
    }

    uploadFragment = async (
        file: File,
        uploadProgress: (percentage: number) => void,
    ): Promise<Result<UploadFile, string>> => {
        const result = await createFragmentUploadFile(
            this.accessToken,
            this.projectID,
        );
        const uploadFile = result.value;

        if (!uploadFile) {
            return {
                value: undefined,
                error: "Unable to start fragment upload session",
            };
        }
        const chunkCount = Math.ceil(file.size / UPLOAD_CHUNK_SIZE);
        for (let i = 0; i < chunkCount; i += 1) {
            // eslint-disable-next-line no-await-in-loop
            const response = await this.uploadChunk(uploadFile, file, i);
            uploadProgress(i / chunkCount);
            if (!response.value) {
                return response;
            }
        }
        const response = await uploadFileClose(this.accessToken, uploadFile.id);
        if (response.value && response.value.size === file.size) {
            return response;
        }
        return {
            value: undefined,
            error: response.error
                ? response.error
                : "Unable to finish fragment upload",
        };
    };

    uploadChunk = async (
        uploadFile: UploadFile,
        file: File,
        chunkNumber: number,
    ): Promise<Result<UploadFile, string>> => {
        const chunk = file.slice(
            chunkNumber * UPLOAD_CHUNK_SIZE,
            (chunkNumber + 1) * UPLOAD_CHUNK_SIZE,
        );
        const fileReader = new FileReader();
        const promise = new Promise<Result<UploadFile, string>>(
            (resolve, reject) => {
                fileReader.onload = async () => {
                    const buffer = fileReader.result as ArrayBuffer;
                    const response = await uploadFileWrite(
                        this.accessToken,
                        uploadFile.id,
                        buffer,
                    );
                    if (response.error) {
                        reject(response.error);
                        return;
                    }
                    resolve(response);
                };
                fileReader.readAsArrayBuffer(chunk);
            },
        );
        return promise;
    };

    uploadThumbnail = async (
        file: File,
        uploadProgress: (percentage: number) => void,
    ): Promise<Result<UploadFile, string>> => {
        const result = await createThumbnailUploadFile(
            this.accessToken,
            this.projectID,
        );
        const uploadFile = result.value;
        if (!uploadFile) {
            return {
                value: undefined,
                error: "Unable to start upload session",
            };
        }
        const chunkCount = Math.ceil(file.size / UPLOAD_CHUNK_SIZE);
        for (let i = 0; i < chunkCount; i += 1) {
            // eslint-disable-next-line no-await-in-loop
            const response = await this.uploadChunk(uploadFile, file, i);
            uploadProgress(i / chunkCount);
            if (!response.value) {
                return response;
            }
        }

        const response = await uploadFileClose(this.accessToken, uploadFile.id);
        if (response.value && response.value.size === file.size) {
            return response;
        }
        return {
            value: undefined,
            error: response.error
                ? response.error
                : "Unable to finish thumbnail upload",
        };
    };

    uploadVariant = async (
        file: File,
        uploadProgress: (percentage: number) => void,
    ): Promise<Result<UploadFile, string>> => {
        const result = await createVariantUploadFile(
            this.accessToken,
            this.projectID,
            ".vtt",
        );
        const uploadFile = result.value;
        if (!uploadFile) {
            return {
                value: undefined,
                error: "Unable to start upload session",
            };
        }
        const chunkCount = Math.ceil(file.size / UPLOAD_CHUNK_SIZE);
        for (let i = 0; i < chunkCount; i += 1) {
            // eslint-disable-next-line no-await-in-loop
            const response = await this.uploadChunk(uploadFile, file, i);
            uploadProgress(i / chunkCount);
            if (!response.value) {
                return response;
            }
        }

        const response = await uploadFileClose(this.accessToken, uploadFile.id);
        if (response.value && response.value.size === file.size) {
            return response;
        }
        return {
            value: undefined,
            error: response.error
                ? response.error
                : "Unable to finish variant upload",
        };
    };

    uploadMediaFile = async (
        file: File,
        uploadProgress: (percentage: number) => void,
        mediaFileType?: MediaFileType,
        key?: string,
        extension?: string,
    ): Promise<Result<UploadFile, string>> => {
        if (!mediaFileType || !key || !extension) {
            return {
                value: undefined,
                error: "input undefined",
            };
        }
        const result = await createMediaUploadFile(
            this.accessToken,
            this.projectID,
            mediaFileType,
            key,
            extension,
        );
        if (result.error) {
            console.error(result.error);
        }
        const uploadFile = result.value;
        if (!uploadFile) {
            return {
                value: undefined,
                error: "Unable to start MediaFile upload session",
            };
        }
        const chunkCount = Math.ceil(file.size / UPLOAD_CHUNK_SIZE);
        for (let i = 0; i < chunkCount; i += 1) {
            // eslint-disable-next-line no-await-in-loop
            const response = await this.uploadChunk(uploadFile, file, i);
            uploadProgress(i / chunkCount);
            if (!response.value) {
                return response;
            }
        }
        const response = await uploadFileClose(this.accessToken, uploadFile.id);
        if (response.value && response.value.size === file.size) {
            return response;
        }
        return {
            value: undefined,
            error: response.error
                ? response.error
                : "Unable to finish fragment upload",
        };
    };
}

export default UploadManager;
